import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MapPin, CalendarDays, Clock, Users, Banknote, Loader2, Search, Zap, Repeat, Award } from "lucide-react";
import { motion } from "framer-motion";
import { getSportColor } from "@/lib/sports";
import SportAutocomplete from "@/components/SportAutocomplete";
import LocationAutocomplete from "@/components/LocationAutocomplete";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [sport, setSport] = useState(searchParams.get("sport_type") || "");
  const [city, setCity] = useState(searchParams.get("city") || "");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = { upcoming: "true" };
      if (sport && sport !== "all") params.sport_type = sport;
      if (city && city !== "all") params.city = city;
      if (query) params.q = query;
      const res = await api.get("/events", { params });
      setEvents(res.data);
    } catch { setEvents([]); }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, []); // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight flex items-center gap-3" data-testid="events-page-title">
            <Zap className="w-8 h-8 text-court-orange" />Events
          </h1>
          <p className="text-muted-foreground mt-1">Tournaments, clinics, and pickup games</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-8 p-4 bg-white border-2 border-slate-200" data-testid="event-filters">
        <div className="flex-1">
          <Input data-testid="event-search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search events..." className="h-11 rounded-none border-2" onKeyDown={e => e.key === "Enter" && fetchEvents()} />
        </div>
        <SportAutocomplete value={sport} onValueChange={v => setSport(v)} placeholder="All Sports" triggerClassName="w-full md:w-52" />
        <LocationAutocomplete value={city} onValueChange={v => setCity(v)} placeholder="All Locations" triggerClassName="w-full md:w-56" />
        <Button data-testid="event-search-btn" onClick={fetchEvents}
          className="bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide px-8 h-11 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
          <Search className="w-4 h-4 mr-2" />Search
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>
      ) : events.length === 0 ? (
        <div className="text-center py-20 border-2 border-dashed border-slate-200">
          <Zap className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-xl text-muted-foreground uppercase">No upcoming events</p>
          <p className="text-sm text-muted-foreground mt-2">Check back soon or adjust your filters</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
          {events.map((evt, i) => {
            const isFree = !evt.price || evt.price <= 0;
            const spotsLeft = Math.max(0, evt.capacity - (evt.registered_count || 0));
            return (
              <motion.div key={evt.event_id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }} onClick={() => navigate(`/events/${evt.event_id}`)}
                className="bg-white border-b-4 border-r-4 border-black overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-200"
                data-testid={`event-card-${evt.event_id}`}>
                <div className="h-44 overflow-hidden relative">
                  <img src={evt.image || "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600"} alt={evt.title}
                    className="w-full h-full object-cover" />
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    <Badge className={`${getSportColor(evt.sport_type)} text-[10px] font-heading uppercase tracking-wider`}>{evt.sport_type}</Badge>
                    {isFree && <Badge className="bg-lime-volt text-black text-[10px] font-heading uppercase">Free</Badge>}
                    {evt.recurrence_type && evt.recurrence_type !== "NONE" && (
                      <Badge className="bg-purple-500 text-white text-[10px] font-heading uppercase"><Repeat className="w-3 h-3 mr-0.5 inline" />{evt.recurrence_type}</Badge>
                    )}
                  </div>
                  {spotsLeft <= 5 && spotsLeft > 0 && (
                    <Badge className="absolute top-3 right-3 bg-red-500 text-white text-[10px] font-heading uppercase">{spotsLeft} spots left</Badge>
                  )}
                  {spotsLeft <= 0 && (
                    <Badge className="absolute top-3 right-3 bg-slate-800 text-white text-[10px] font-heading uppercase">Full</Badge>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-bold uppercase tracking-tight mb-2">{evt.title}</h3>
                  <div className="space-y-1.5 text-sm text-muted-foreground">
                    <p className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-court-orange" />{evt.event_date}</p>
                    <p className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{evt.start_time} - {evt.end_time}</p>
                    {evt.venue_name && <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{evt.venue_name}, {evt.venue_city}</p>}
                    {evt.coach_name && <p className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-court-orange" />Coach: {evt.coach_name}</p>}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{evt.registered_count || 0}/{evt.capacity}</span>
                      <span className="font-heading text-lg font-bold text-court-orange">
                        {isFree ? "FREE" : `₱${evt.price?.toFixed(0)}`}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
